import React, { FC } from 'react';

import { Box, IconButton, Tooltip, Typography } from '@mui/material';

import {
  AdminLegendVariant,
  IAdminRequestData,
  ILongTermContractModel,
  adminActions,
  isLoadingSelector,
} from '@pharmaplan/common';
import strings from '../../../../localization';
import PharmacyCard from '../../../SelfService/PharmacyCard';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { renderScreen } from '../../../../actions/drawerActions';
import { ScreenTypes, SkeletonVariants } from '../../../../helpers/Constants';

import AdminLongTermUserRates from '../../../common/AdminHourlyRateFooter/AdminLongTermUserRates';
import { ReactComponent as RequestBookingIcon } from '../../../../assets/svg/requestBookingIcon.svg';
import { LocationText } from '../../AvailableWorkshifts/AvailableWorkshifts';
import AdminHourlyRateFooter from '../../../common/AdminHourlyRateFooter';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import useSelfService from '../../../../hooks/useSelfService';
import WithSkeleton from '../../../common/WithSkeleton';
import useGetItems from '../../../../hooks/useGetItems';

import styles from './styles';

interface ISingleRequest {
  event: IAdminRequestData;
  successAction: string;
}

const SingleRequest: FC<ISingleRequest> = ({ event, successAction }) => {
  const dispatch = useAppDispatch();
  const { isSelfService } = useSelfService();

  const loading = useAppSelector((state) =>
    isLoadingSelector([successAction], state));

  const SkeletonTypography = WithSkeleton({
    successAction,
    variant: SkeletonVariants.text,
  })(Typography);

  const {
    workshiftId,
    pharmacyHourlyRate: hourlyRate,
    count,
    pharmacyId,
    logoId,
    city,
    softwareName,
    startDate,
    replacementType,
    isAutoSyncRecord,
    weekDayHourlyRate,
    weekendHourlyRate,
    contractEndDate,
    pharmacyWeekDayHourlyRate,
    pharmacyWeekendHourlyRate,
    contractStartDate,
    campaignName = '',
    creditsLeft,
    pharmacistHourlyRate,
  } = event ?? {};

  const isPromoRequest = !!creditsLeft;
  const discountTooltip = strings.formatString(strings.thisUserLinkedWithCampaignFormat, campaignName ?? '') as string;

  const { getImage } = useGetItems();

  const goToPharmacistRequestedList = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.stopPropagation();
    dispatch(
      renderScreen({
        screenNumber: 2,
        screenType: ScreenTypes.requestedList,
        eventId: workshiftId,
        extraParameters: {
          isAutoSyncRecord,
        },
      }),
    );
  };

  const goToRequestedList = () => {
    dispatch(
      renderScreen({
        screenNumber: 1,
        screenType: ScreenTypes.requestedList,
        date: startDate,
      }),
    );
  };

  const goToViewDetails = () => {
    dispatch(
      renderScreen({
        screenNumber: 3,
        screenType: ScreenTypes.availableWorkshift,
        eventId: workshiftId,
        type: AdminLegendVariant.requested,
        back: goToRequestedList,
        pharmacyId,
        extraParameters: {
          isAutoSyncRecord,
        },
      }),
    );
  };

  const longTermShiftContract: ILongTermContractModel = {
    startDate: contractStartDate ?? '',
    endDate: contractEndDate ?? '',
    weekDayPaidHourlyRate: weekDayHourlyRate ?? 0,
    weekendPaidHourlyRate: weekendHourlyRate ?? 0,
    longTermShiftContractId: contractEndDate ?? '',
    allowanceNotes: '',
    createDate: '',
    updateDate: null,
  };

  const { customPharmacistChip, customPharmacyChip } = AdminLongTermUserRates({
    pharmacyWeekDayHourlyRate,
    pharmacyWeekendHourlyRate,
    contractStartDate,
    weekDayHourlyRate,
    weekendHourlyRate,
    replacementType,
    successAction,
  });

  const params = isSelfService
    ? {
      software: softwareName,
    }
    : {
      pharmacistHourlyRate,
    };

  return (
    <Box sx={styles.mainContainer}>
      <PharmacyCard
        longTermShiftContract={longTermShiftContract}
        isAutoSyncRecord={isAutoSyncRecord}
        showDiscountIcon={isPromoRequest}
        discountTooltip={discountTooltip}
        customContainerStyle={styles.container}
        successAction={adminActions.getAdminEvent}
        customTitleStyle={styles.title}
        showPharmacyName
        contentClick={goToViewDetails}
        CustomRightItem={(
          <IconButton
            onClick={goToPharmacistRequestedList}
            sx={styles.iconContainer}
          >
            <Box sx={styles.iconSubContainer}>
              {!loading && count && (
                <Box sx={styles.countContainer}>
                  <Typography sx={styles.countText}>
                    +
                    {count}
                  </Typography>
                </Box>
              )}
              <Tooltip title={strings.bookingRequests}>
                <RequestBookingIcon />
              </Tooltip>
            </Box>
          </IconButton>
        )}
        CustomContent={(
          <LocationText
            containerStyle={styles.footerContainer}
            label={
              <SkeletonTypography sx={styles.footer}>{city}</SkeletonTypography>
            }
          />
        )}
        event={{ ...event, hourlyRate, logoImage: getImage(logoId ?? '') }}
        CustomFooter={(
          <AdminHourlyRateFooter
            pharmacyHourlyRate={hourlyRate}
            successAction={successAction}
            replacementType={replacementType}
            customPharmacistChip={customPharmacistChip}
            customPharmacyChip={customPharmacyChip}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...params}
          />
        )}
        type={AdminLegendVariant.requested}
      />
    </Box>
  );
};

export default SingleRequest;
